<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { watchDebounced } from '@vueuse/core'
import debounce from 'lodash/debounce'
import { useDevice } from '#imports'
import { SPECIAL_KEYS } from '~/constants'
import { formatMoney, NumberUtils } from '~/utils'
import { useBaseInput } from '~composables/common/use-base-input'
import { useRuntimeConfig } from '#app'

const { useField, allowNumber } = useBaseInput()
const { isIos, isMobile } = useDevice()

const emits = defineEmits(['update:modelValue', 'handleKeyup', 'onChangeInput', 'onClearInput', 'onFocusOut'])

const props = defineProps({
  iconPrepend: { type: String, default: '' },
  name: { type: String, default: '' },
  className: { type: String, default: '' },
  vndCurrency: { type: Boolean, default: false },
  type: { type: String, default: 'text' },
  label: { type: String, default: '' },
  isLabelFloating: { type: Boolean, default: false },
  inputValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  hasRequiredMark: { type: Boolean, default: false },
  maxLength: { type: Number, default: undefined },
  isUppercase: { type: Boolean, default: false },
  errorCustom: { type: String, default: '' },
  currency: { type: Boolean, default: false },
  creditRate: { type: Number, default: 0 },
  isNumberOnly: { type: Boolean, default: false },
  isShowedIconDelete: { type: Boolean, default: false },
  currencyName: { type: String, default: '' },
  pattern: { type: RegExp, default: undefined },
  inputMode: { type: String, default: 'text' },
  shouldTrim: { type: Boolean, default: false }
})
const config = useRuntimeConfig()
const { FLAT_CURRENCY_UNIT } = config.public
const inputRef = ref<HTMLInputElement | null>(null)
const inputType = ref(props.type)
const isShowPassword = ref(false)
const currentErrorMessage = ref('')
const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  resetField
} = useField(props.name, undefined, {
  initialValue: props.inputValue
})
const validationListeners = computed(() => {
  return {
    blur: handleChange,
    change: handleChange,
    input: (event: Event) => handleInput(event)
  }
})

watch(
  () => props.inputValue,
  () => {
    if (props.inputValue) {
      inputValue.value = props.inputValue
    } else {
      resetField({
        value: props.inputValue
      })
    }
  }
)
// Update the error message based on the field's state and error handling logic
watch(
  () => errorMessage.value,
  newErrorMessage => {
    currentErrorMessage.value = newErrorMessage ?? ''
  }
)

// formatNumberWithCommas on paste
watchDebounced(
  () => inputValue.value,
  newValue => {
    if (!newValue) {
      return
    }
    if (props.currency && !isNaN(Number(newValue?.replace(/[^\d.-]/g, '')))) {
      inputValue.value =
        Number(newValue?.replace(/[^\d.-]/g, '')) !== 0
          ? NumberUtils.formatNumberWithCommas(Number(newValue?.replace(/[^\d.-]/g, '')) || null)
          : NumberUtils.formatNumberWithCommas(newValue?.replace(/[^\d.-]/g, '') || null)
      debouncedEmit()
    }
    if (props.shouldTrim) {
      inputValue.value = newValue.trim()
      debouncedEmit()
    }
  },
  { debounce: 1500, immediate: true }
)

const debouncedEmit = debounce(() => {
  emits('onChangeInput', inputValue.value)
})

const handleInput = debounce((event: Event): void => {
  emits('update:modelValue', inputValue.value)
  if (errorMessage.value) {
    handleChange(event)
  } else {
    handleChange(event, false)
  }
}, 100)

const change = (): void => {
  emits('onChangeInput', inputValue.value)
}

const togglePassword = (): void => {
  isShowPassword.value = !isShowPassword.value
  inputType.value = isShowPassword.value ? 'text' : 'password'
}
const clearInput = (): void => {
  inputValue.value = ''
  emits('onClearInput')
}
const toVndAmount = computed(() => {
  return formatMoney(inputValue.value?.replace(props.currencyName, ''), 1000)
})
const formatPlaceholder = computed(() => {
  if (props.isLabelFloating) {
    return ''
  }
  return props.placeholder ? props.placeholder : ''
})

const handleKeyup = debounce((keyboardEvent: KeyboardEvent): void => {
  if (props.type === 'tel') {
    allowNumber(props.type, keyboardEvent)
    filterNumericInput(keyboardEvent)
  }
  emits('update:modelValue', inputValue.value)
  const inputTargetValue = (keyboardEvent.target as HTMLInputElement).value
  if (inputValue.value !== inputTargetValue) {
    return
  }
  handleConvertUpperCase()
  emits('handleKeyup', inputValue.value)
}, 100)

const handleConvertUpperCase = (): void => {
if (props.isUppercase) {
    inputValue.value = inputValue.value.toUpperCase()
  }
}

const onPaste = (event: ClipboardEvent): void => {
  if (event.clipboardData) {
    const value = event.clipboardData.getData('text')
    if ((props.type === 'number' || props.isNumberOnly) && isNaN(Number(value))) {
      event.preventDefault()
    }
  }
}

const onBlur = (blurEvent: FocusEvent) => {
  const { target: inputElement } = blurEvent
  if (inputElement) {
    emits('onFocusOut', (inputElement as HTMLInputElement).value)
  }
  handleBlur(blurEvent)
}

const classObject = computed(() => ({
  'base-input--password': props.type === 'password',
  disabled: props.disabled,
  'input-error': currentErrorMessage.value.length > 0 || props.errorCustom,
  'floating-label': props.isLabelFloating,
  'has-value': inputValue.value,
  'with-prepend-icon': props.iconPrepend
}))

const filterNumericInput = (keyboardEvent: KeyboardEvent): void => {
  const target = keyboardEvent.target as HTMLInputElement
  const isNumberRegex = /^[0-9]+$/
  const isNumberRegexObject = new RegExp(isNumberRegex)
  if (target.value && !isNumberRegexObject.test(target.value)) {
    inputValue.value = target.value?.replace(/[^0-9]/g, '')?.trim()
  } else {
    inputValue.value = target.value
  }
}
const onKeyDown = (event: KeyboardEvent): void => {
  if (props.pattern) {
    if (!props.pattern.test(event.key) && !SPECIAL_KEYS.includes(event.key)) {
      const specials = ['altKey', 'metaKey', 'ctrlKey']
      const lowerKey = event.key.toLowerCase()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (['c', 'v'].includes(lowerKey) && specials.filter(special => (event as any)[special]).length > 0) {
        return
      }
      event.preventDefault()
    }
  }
}
</script>
<template>
  <div class="base-input" :class="[classObject, className]" :name="name">
    <label v-if="label" :for="name">
      {{ label }}
      <span v-if="hasRequiredMark" class="required-mark">*</span>
    </label>

    <div v-if="inputType !== 'textarea'" :class="['base-input__wrap relative', { 'ios-device': isIos }]">
      <img v-if="iconPrepend" :src="iconPrepend" class="prepend-icon" :alt="`${name} icon`" />

      <input
        :id="name"
        ref="inputRef"
        v-model="inputValue"
        :inputMode="inputMode"
        :type="inputType"
        :maxlength="maxLength"
        :disabled="disabled"
        :placeholder="formatPlaceholder"
        :class="{ 'text-uppercase': isUppercase, 'ios-device': isIos }"
        autocomplete="new-password"
        @blur="onBlur"
        @change="change"
        @keydown="onKeyDown"
        @keypress="allowNumber(inputType, $event)"
        @keyup="handleKeyup"
        @paste="onPaste"
        v-on="validationListeners"
      />

      <div v-if="currencyName && inputValue" class="currency-name">
        <span>{{ inputValue }}</span>
        {{ currencyName }}
      </div>

      <i v-if="type === 'password'" class="eye" :class="{ show: isShowPassword }" @click="togglePassword()"></i>
      <div
        v-if="inputValue?.length && !disabled && isShowedIconDelete"
        :class="['icon-block', { 'margin-lock': type === 'password' }, { 'd-none': type === 'hidden' }]"
        @click="clearInput()"
      >
        <span class="icon-block icon-close"></span>
      </div>
      <i v-if="disabled" class="disable-icon"></i>

      <slot></slot>

      <span v-if="currentErrorMessage" class="error">{{ errorMessage }}</span>
    </div>

    <div v-else class="base-input__wrap base-input__wrap--reverse">
      <textarea
        v-model="inputValue"
        :name="name"
        :placeholder="formatPlaceholder"
        @blur="handleBlur"
        @change="change"
        @keyup="handleKeyup"
        @paste="onPaste"
        v-on="validationListeners"
      ></textarea>
      <label>{{ label }}</label>
      <span v-if="currentErrorMessage" class="error">{{ errorMessage }}</span>
    </div>

    <span v-if="!currentErrorMessage && errorCustom" class="error">
      {{ errorCustom }}
    </span>

    <p
      v-if="vndCurrency"
      :class="[
        'base-input__vnd',
        { 'has-icon-delete': inputValue?.length && !disabled && isShowedIconDelete && !isMobile }
      ]"
    >
      = {{ toVndAmount }} {{ FLAT_CURRENCY_UNIT }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
@import '~scssAsset/components/common/base-input.scss';
</style>
